import { fromPromise } from 'xstate';

import { type Predictions } from '@sg/garnish';

import { locationSearchLogger } from '../../../helpers';
import {
  fetchPredictions,
  searchLocationsByRestaurantName,
  selectPrediction as selectPredictionService,
} from '../../../services';
import { type Event, type LocationGroups, type PositionCoordinates } from '../../../types';
import { type SearchContext } from '../locations';

import type { Client } from 'urql';

// ─────────────────────────────────────────────────────────────────────────────

export const getPredictionsAndRestaurantsActor = fromPromise(
  async ({ input }: { input: PredictionsAndRestaurantsActorInput }) => {
    const { client, predictionsSearchQuery, currentPosition, context } = input;

    if (!predictionsSearchQuery) {
      const emptyResult: { predictions: Predictions; locations: LocationGroups } = {
        predictions: [],
        locations: { pickup: [], outpost: [] },
      };

      return emptyResult;
    }

    const near = currentPosition
      ? `${currentPosition.latitude},${currentPosition.longitude}`
      : undefined;

    const [predictionsResult, locationsResult] = await Promise.allSettled([
      fetchPredictions(predictionsSearchQuery, { near }),
      searchLocationsByRestaurantName({ client, context }),
    ]);

    const didPredictionsFail = predictionsResult.status === 'rejected';
    const didLocationsFail = locationsResult.status === 'rejected';

    // Show whichever lookup succeeded; fail the search only when neither has data.
    if (didPredictionsFail && didLocationsFail) {
      throw predictionsResult.reason;
    }

    if (didPredictionsFail) {
      locationSearchLogger.error('Failed to fetch place predictions', predictionsResult.reason);
    }

    if (didLocationsFail) {
      locationSearchLogger.error('Failed to fetch locations by name', locationsResult.reason);
    }

    return {
      predictions: didPredictionsFail ? [] : predictionsResult.value.predictions,
      locations: didLocationsFail ? EMPTY_LOCATIONS : locationsResult.value,
    };
  },
);

export const selectPredictionActor = fromPromise(
  async ({ input }: { input: SelectPredictionActorInput }) =>
    selectPredictionService({ predictions: input.predictions, event: input.event }),
);

// ─── Constants ───────────────────────────────────────────────────────────────

const EMPTY_LOCATIONS: LocationGroups = { pickup: [], outpost: [] };

// ─── Types ───────────────────────────────────────────────────────────────────

export type PredictionsAndRestaurantsActorInput = {
  client: Client;
  predictionsSearchQuery: string | undefined;
  currentPosition: PositionCoordinates | undefined;
  context: SearchContext;
};

export type SelectPredictionActorInput = {
  predictions: Predictions;
  event: Extract<Event, { type: 'SELECT_PREDICTION' }>;
};
